import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import Head from 'next/head'
import styled from 'styled-components'
import { TextHeadline2, TextBody, Link as BuiLink, colors } from '@blocket/blocket-ui'

import SkipLink from 'components/sharedComponents/SkipLink'
import { FullHeightWrapper as FHW } from 'components/sharedComponents/LayoutStyles'
import Footer from 'components/sharedComponents/Footer'
import TabBar from 'components/sharedComponents/TabBar'
import { trackingError404page } from 'tracking/errorPages'
import staticFilesUrl from 'constants/staticFiles'

const ImageContainer = styled.div`
  margin-bottom: 8px;
  @media (max-height: 500px) {
    .errorImage {
      display: none !important;
    }
  }
`

const FullHeightWrapper = styled(FHW)`
  min-height: ${(props) => props.showNoTabBar && 'calc(100vh - 64px)'};
`

const Link = styled(BuiLink)`
  text-decoration: underline;
`

export const Illustration = ({ image, maxWidth = 280 }) => (
  <img
    style={{ maxWidth }}
    aria-hidden
    alt=""
    src={`${staticFilesUrl}/assets/images/${image}.png`}
    srcSet={
      `${staticFilesUrl}/assets/images/${image}@2x.png 2x, ` +
      `${staticFilesUrl}/assets/images/${image}@3x.png 3x`
    }
  />
)

Illustration.propTypes = {
  image: PropTypes.string,
  maxWidth: PropTypes.number,
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: ${colors.white100};
  flex: 1 1 auto;
  margin: 16px;
  padding: 60px 0px;
`

export const ErrorImage = ({ image, maxWidth = 280 }) => (
  <ImageContainer>
    <Illustration image={image} maxWidth={maxWidth} />
  </ImageContainer>
)

ErrorImage.propTypes = {
  image: PropTypes.string.isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

const ErrorComponent = ({ error, showNoTabBar, goBackUrl }) => {
  useEffect(() => {
    if (error === '404') {
      trackingError404page()
    }
  }, [error])

  const getComponent = (image, heading, bodyText, linkText, linkUrl = '/') => (
    <Container id="skip-tabbar">
      <ErrorImage image={image} />
      <TextHeadline2>{heading}</TextHeadline2>
      <TextBody style={{ margin: '16px', maxWidth: '485px' }}>
        {bodyText.map((text) => (
          <p key={text} style={{ textAlign: 'center' }}>
            {text}
          </p>
        ))}
      </TextBody>
      {linkText && (
        <Link to={linkUrl} external>
          {linkText}
        </Link>
      )}
    </Container>
  )

  let child
  let imageName
  let header
  let bodyText
  let linkText
  let linkUrl
  const noIndexPages = ['no_adwatch_ads', 'store_not_found', '404', 'ad not found']
  switch (error) {
    case 'no_adwatch_ads':
      imageName = 'oops'
      header = 'Här var det tomt!'
      bodyText = ['Inga annonser hittades för denna bevakning']

      return getComponent(imageName, header, bodyText, linkText)

    case 'failed_to_fetch':
      imageName = 'hittade-inga-annonser'
      header = 'Ånej!'
      bodyText = ['Tyvärr verkar det inte funka just nu. Försök igen lite senare.']
      child = getComponent(imageName, header, bodyText, linkText)
      break

    case '404':
      imageName = 'oops'
      header = 'Hoppsan, 404 Error'
      bodyText = ['Vi kunde inte hitta sidan som du letade efter.']
      linkText = 'Hitta tillbaka till Blocket'
      child = getComponent(imageName, header, bodyText, linkText)
      break

    case '500':
      imageName = 'needhelp'
      header = 'Ånej!'
      bodyText = ['Något blev fel. Försök igen.']
      linkText = 'Tillbaka till startsidan'
      child = getComponent(imageName, header, bodyText, linkText)
      break

    case 'ad not found':
      imageName = 'oops'
      header = 'Hittade inte annonsen…'
      bodyText = [
        'Den här varan finns inte på Blocket. Förmodligen beror det på att någon hann före dig eller så har annonsören tagit bort sin annons.',
        'Om du just lagt in eller ändrat den här annonsen, kom ihåg att det kan dröja en halvtimme innan den visas.',
      ]
      linkText = 'Till startsidan'
      child = getComponent(imageName, header, bodyText, linkText)
      break

    case 'function_turned_off':
      imageName = 'oops'
      header = 'Oops! Någonting gick fel!'
      bodyText = ['Den här funktionen fungerar inte just nu.']
      linkText = 'Gå tillbaka'
      linkUrl = goBackUrl
      child = getComponent(imageName, header, bodyText, linkText, linkUrl)
      break

    case 'something_went_wrong':
      imageName = 'oops'
      header = 'Ånej!'
      bodyText = ['Något blev fel']
      linkText = 'Gå tillbaka'
      linkUrl = goBackUrl
      child = getComponent(imageName, header, bodyText, linkText, linkUrl)
      break

    default:
      imageName = 'oops'
      header = 'Ånej!'
      bodyText = ['Något blev fel. Försök igen.']
      linkText = 'Tillbaka till startsidan'
      child = getComponent(imageName, header, bodyText, linkText)
      break
  }
  return (
    <FullHeightWrapper data-testid="error-component" showNoTabBar>
      {noIndexPages.indexOf(error) !== -1 && (
        <Head>
          <meta key="robots" name="robots" content="noindex, nofollow" />
          <meta key="googlebot" name="googlebot" content="none" />
        </Head>
      )}
      <SkipLink to="#skip-tabbar" />
      {showNoTabBar ? null : <TabBar />}
      {child}
      <Footer />
    </FullHeightWrapper>
  )
}

ErrorComponent.propTypes = {
  error: PropTypes.string,
  showNoTabBar: PropTypes.bool,
  goBackUrl: PropTypes.string,
}

export default ErrorComponent
